import React, { useState } from 'react';
import Button from '../Button';
import { Menu, MenuItem, MenuSeparator } from '../Menu';
import { ALL_COLUMNS, STATUSES, STATUS_LABELS } from './columns';
import {
  ROSTER_WORDING as W, actionsLabel, markAsLabel, rosterCountLabel, statusFilterSummary,
} from '../../domain/roster';
import { figureOrDash } from '../../domain/reading';
import s from './students.module.css';

// The controls above the students table: add, bulk actions, status filter,
// column picker, sort, search.
//
// Both menus are held here rather than in the page, because only one may be
// open at a time and something has to own that.
//
// The status filter is a checkbox list in a `Menu` panel (#574) rather than a
// `<select>`, the shape the Columns picker uses for "more than one of these".
//
// `countKnown` is false while the roster is unread: the count is the dash,
// not "0 active" (#637).

export default function StudentToolbar({
  count, countKnown = true, statusFilter, onToggleStatusFilter, onSetStatusFilter,
  search, onSearchChange,
  sortAsc, onToggleSort,
  columnIds, onToggleColumn,
  selectedCount, busy, onApplyStatus, onDeleteSelected = null,
  onAddStudent,
}) {
  const [openMenu, setOpenMenu] = useState(null); // null | 'actions' | 'columns' | 'status'
  const close = () => setOpenMenu(null);
  const toggle = (name) => setOpenMenu((cur) => (cur === name ? null : name));
  const allStatusesOn = statusFilter.size === STATUSES.length;
  const statusSummary = statusFilterSummary(statusFilter, STATUSES, (v) => STATUS_LABELS[v]);

  return (
    <>
      <div className={s.bar}>
        <span className={s.count}>
          {figureOrDash(countKnown, rosterCountLabel(count, statusFilter, STATUSES))}
        </span>
      </div>

      <div className={s.bar}>
        <Button onClick={onAddStudent}>{W.addNew}</Button>

        <Menu
          open={openMenu === 'actions'}
          onClose={close}
          align="left"
          trigger={(
            <Button
              variant="secondary"
              disabled={selectedCount === 0 || busy}
              onClick={() => toggle('actions')}
            >
              {actionsLabel(selectedCount)}
            </Button>
          )}
        >
          {STATUSES.map((status) => (
            <MenuItem key={status} onClick={() => { onApplyStatus(status); close(); }}>
              {markAsLabel(status)}
            </MenuItem>
          ))}
          {/* `Delete Students`, last, where MMS puts it. MMS's bulk menu runs
              Assign/Unassign Tags, Update Lesson Settings, Edit Make-Up
              Credits, Email & SMS Reminders, Apply Lesson Prices and Package
              Time, Download, Set Status, Delete Students (#175); this one has
              Set Status and Delete Students, in that order. A per-student
              Set Make-Up Credits is on the student page (#178).

              As in MMS, delete is on this menu and nowhere else — not on the
              student page or its drawers. The gesture is tidying up the roster,
              and for most students that is what it does (planStudentDeletion
              in domain/people.js). */}
          {onDeleteSelected ? (
            <>
              <MenuSeparator />
              <MenuItem onClick={() => { onDeleteSelected(); close(); }}>
                {W.deleteStudents}
              </MenuItem>
            </>
          ) : null}
        </Menu>

        <span className={s.spacer} />

        <Menu
          open={openMenu === 'status'}
          onClose={close}
          align="left"
          panelClassName={s.checkList}
          trigger={(
            <Button
              variant="secondary"
              className={s.control}
              // Visible text is the compact summary a sighted teacher reads
              // ("Active, Trial"); the accessible name adds what the control
              // is, since the summary alone does not say "filter" to someone
              // who cannot see the toolbar around it.
              aria-label={`${W.filterLabel}: ${statusSummary}`}
              onClick={() => toggle('status')}
            >
              {statusSummary}
            </Button>
          )}
        >
          {/* "All statuses" is its own checkbox, ticked exactly when every
              status below is, rather than an option replacing the others. */}
          <label className={s.checkItem}>
            <input
              type="checkbox"
              checked={allStatusesOn}
              onChange={() => onSetStatusFilter(allStatusesOn ? [] : STATUSES)}
            />
            {W.allStatuses}
          </label>
          <MenuSeparator />
          {STATUSES.map((v) => (
            <label key={v} className={s.checkItem}>
              <input
                type="checkbox"
                checked={statusFilter.has(v)}
                onChange={() => onToggleStatusFilter(v)}
              />
              {STATUS_LABELS[v]}
            </label>
          ))}
        </Menu>

        <Menu
          open={openMenu === 'columns'}
          onClose={close}
          panelClassName={s.checkList}
          trigger={(
            <Button variant="secondary" onClick={() => toggle('columns')}>{W.columns}</Button>
          )}
        >
          {ALL_COLUMNS.map((c) => (
            <label key={c.id} className={s.checkItem}>
              <input
                type="checkbox"
                checked={columnIds.has(c.id)}
                disabled={c.locked}
                onChange={() => onToggleColumn(c.id)}
              />
              {c.label}
            </label>
          ))}
        </Menu>

        <Button variant="secondary" onClick={onToggleSort}>
          {sortAsc ? W.sortAscending : W.sortDescending}
        </Button>

        <input
          className={s.control}
          placeholder={W.search}
          aria-label={W.searchLabel}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>
    </>
  );
}
